import {
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { form, FormField, maxLength, required, requiredError, validate } from '@angular/forms/signals';
import { RouterLink } from '@angular/router';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { finalize } from 'rxjs';
import { AlertaService } from '../../../../../shared/utils/alerta.service';
import { cpfValido } from '../../../../../shared/utils/documento.util';
import { LucideModule } from '../../../../../shared/modules/lucide.module';
import { DependenteResponse, NovoDependente, PARENTESCOS } from '../../../models/dependente.model';
import { MeusDadosService } from '../../../services/meus-dados.service';
import {
  ErroDependente,
  MeusDependentesService,
  mensagemErroDependente,
} from '../../../services/meus-dependentes.service';
import type { PerfilCidadao } from '../../../services/perfil-cidadao.service';

type CampoDadosPessoais = keyof PerfilCidadao;

/**
 * Só os campos que a API pode preencher. Senha, RG e raça/cor ficam fora da conta: nunca vêm
 * do cadastro, e com eles ninguém passaria de 83%. Voltam com a change de gravação.
 */
const CAMPOS_POR_ETAPA: readonly CampoDadosPessoais[][] = [
  ['nomeCompleto', 'genero', 'dataNascimento', 'cpf'],
  ['enderecoCompleto', 'numero', 'cep', 'bairro', 'estado', 'cidade'],
  ['email', 'telefoneCelular01'],
];

const PERFIL_VAZIO: PerfilCidadao = {
  nomeCompleto: '',
  nomeSocial: '',
  genero: '',
  racaCor: '',
  dataNascimento: '',
  rg: '',
  cpf: '',
  enderecoCompleto: '',
  numero: '',
  complemento: '',
  cep: '',
  bairro: '',
  estado: '',
  cidade: '',
  email: '',
  senha: '',
  telefoneResidencial: '',
  telefoneCelular01: '',
  telefoneCelular02: '',
};

const DEPENDENTE_VAZIO: NovoDependente = {
  nomeCompleto: '',
  nomeSocial: '',
  parentesco: '',
  dataNascimento: '',
  genero: '',
  racaCor: '',
  rg: '',
  cpf: '',
};

const TITULO_INDISPONIVEL = 'Atualização ainda não disponível';

const NOME_OBRIGATORIO = 'Informe o nome completo.';
const PARENTESCO_OBRIGATORIO = 'Selecione o parentesco.';
const NASCIMENTO_OBRIGATORIO = 'Informe a data de nascimento.';
const NASCIMENTO_FUTURO = 'A data não pode ser futura.';
const CPF_INVALIDO = 'Informe um CPF válido.';

/** A data de hoje em `yyyy-MM-dd`, no fuso do navegador — o formato do `<input type="date">`. */
function hojeIso(): string {
  const agora = new Date();
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  const dia = String(agora.getDate()).padStart(2, '0');

  return `${agora.getFullYear()}-${mes}-${dia}`;
}

/**
 * A aba "Dados pessoais": o cadastro de quem está logado e os dependentes dele.
 *
 * Do cadastro do titular nada é gravado — "Salvar alterações" diz isso ao cidadão em vez de
 * fingir. Só os **dependentes** são gravados: o modal "Adicionar dependente" e o remover da
 * lista falam com `MeusDependentesService`.
 */
@Component({
  selector: 'app-dados-pessoais',
  templateUrl: './dados-pessoais.component.html',
  styleUrls: ['./dados-pessoais.component.css'],
  imports: [FormField, LucideModule, RouterLink, NgxMaskDirective],
  providers: [provideNgxMask({ dropSpecialCharacters: false })],
})
export class DadosPessoaisComponent {
  private readonly alerta = inject(AlertaService);
  private readonly meusDados = inject(MeusDadosService);
  private readonly meusDependentes = inject(MeusDependentesService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly modal = viewChild<ElementRef<HTMLDialogElement>>('modalDependente');
  private readonly botaoAdicionar = viewChild<ElementRef<HTMLButtonElement>>('botaoAdicionar');

  readonly logado = this.meusDados.logado;

  readonly falhou = this.meusDados.falhou;

  readonly carregado = computed(() => this.meusDados.perfil() !== null);

  /**
   * O formulário nasce do cadastro carregado e se refaz quando ele muda ("Tentar novamente",
   * outro login). O que o cidadão digitar fica só na tela.
   */
  readonly dados = linkedSignal<PerfilCidadao>(() => this.meusDados.perfil() ?? PERFIL_VAZIO);
  readonly dadosForm = form(this.dados);

  /**
   * `generos` e `racasCores` também são o catálogo do dependente, e junto de `parentescos`
   * espelham `Dependente` no back-end, que recusa qualquer outro valor: mudar um lado exige
   * mudar o outro.
   */
  readonly generos = ['Feminino', 'Masculino', 'Não binário', 'Prefiro não informar'];
  readonly racasCores = ['Branca', 'Preta', 'Parda', 'Amarela', 'Indígena', 'Não declarada'];
  readonly parentescos = PARENTESCOS;
  readonly estados = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
    'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
  ];

  /**
   * Só a cidade cadastrada, e só com o estado cadastrado selecionado: sem gravação não há o que
   * escolher. A lista de municípios do estado fica para a change de gravação.
   */
  readonly cidades = computed(() => {
    const cadastrado = this.meusDados.perfil();
    const estado = this.dados().estado;

    return cadastrado?.cidade && estado && estado === cadastrado.estado ? [cadastrado.cidade] : [];
  });

  readonly progressoEtapas = computed(() => {
    const valores = this.dados();

    return CAMPOS_POR_ETAPA.map((campos) => {
      const preenchidos = campos.filter((campo) => valores[campo].trim() !== '').length;
      return Math.round((preenchidos / campos.length) * 100);
    });
  });

  readonly progressoTotal = computed(() => {
    const valores = this.dados();
    const campos = CAMPOS_POR_ETAPA.flat();
    const preenchidos = campos.filter((campo) => valores[campo].trim() !== '').length;

    return Math.round((preenchidos / campos.length) * 100);
  });

  /** `null` na primeira carga ou em falha; lista (talvez vazia) depois. */
  readonly dependentes = this.meusDependentes.dependentes;

  readonly falhouDependentes = this.meusDependentes.falhou;

  readonly dependenteNovo = signal<NovoDependente>({ ...DEPENDENTE_VAZIO });

  readonly dependenteForm = form(this.dependenteNovo, (caminho) => {
    required(caminho.nomeCompleto, { message: NOME_OBRIGATORIO });
    maxLength(caminho.nomeCompleto, 250);
    maxLength(caminho.nomeSocial, 250);

    required(caminho.parentesco, { message: PARENTESCO_OBRIGATORIO });

    required(caminho.dataNascimento, { message: NASCIMENTO_OBRIGATORIO });
    // `yyyy-MM-dd` compara como texto. Em branco, quem reclama é o `required`.
    validate(caminho.dataNascimento, ({ value }) =>
      !value() || value() <= hojeIso() ? null : requiredError({ message: NASCIMENTO_FUTURO }),
    );

    maxLength(caminho.rg, 30);

    // CPF é opcional; informado, precisa ser possível.
    validate(caminho.cpf, ({ value }) =>
      !value().trim() || cpfValido(value()) ? null : requiredError({ message: CPF_INVALIDO }),
    );
  });

  /** As mensagens por campo só aparecem depois da primeira tentativa de salvar. */
  readonly mostrarPendencias = signal(false);

  readonly salvando = signal(false);

  /** O código do dependente que está sendo removido, para desabilitar só o chip dele. */
  readonly removendo = signal<number | null>(null);

  /**
   * O erro do envio, mostrado DENTRO do modal. Não pode ser um alerta: o `<dialog>` aberto com
   * `showModal()` deixa o SweetAlert atrás dele e sem clique.
   */
  readonly erroModal = signal<ErroDependente | null>(null);

  /** O `max` do campo de data. */
  readonly hoje = hojeIso();

  constructor() {
    // Sessão encerrada com o modal aberto (um 401 no envio): a aba passa a mostrar "Sua sessão
    // foi encerrada", e o modal não pode ficar por cima dela.
    effect(() => {
      const dialogo = this.modal()?.nativeElement;

      if (!this.logado() && dialogo?.open) dialogo.close();
    });
  }

  recarregar(): void {
    this.meusDados.recarregar();
  }

  recarregarDependentes(): void {
    this.meusDependentes.recarregar();
  }

  salvar(evento: Event): void {
    evento.preventDefault();

    void this.alerta.info({
      titulo: TITULO_INDISPONIVEL,
      texto: 'Por enquanto, o portal só mostra os seus dados cadastrais. A alteração ainda não está disponível.',
    });
  }

  /** A mensagem do campo do modal, ou `null` enquanto não há o que mostrar. */
  erroDependente(campo: keyof NovoDependente): string | null {
    if (!this.mostrarPendencias()) return null;

    const estado = this.dependenteForm[campo]();

    return estado.invalid() ? (estado.errors()[0]?.message ?? 'Campo inválido.') : null;
  }

  abrirModalDependente(): void {
    this.dependenteNovo.set({ ...DEPENDENTE_VAZIO });
    this.dependenteForm().reset();
    this.mostrarPendencias.set(false);
    this.erroModal.set(null);

    this.modal()?.nativeElement.showModal();
  }

  fecharModalDependente(): void {
    if (this.salvando()) return;

    this.modal()?.nativeElement.close();
  }

  /** A tecla Esc dispara `cancel` no `<dialog>`; durante o envio o modal não fecha. */
  aoCancelarModal(evento: Event): void {
    if (this.salvando()) evento.preventDefault();
  }

  salvarDependente(evento: Event): void {
    evento.preventDefault();

    if (this.salvando()) return;

    if (!this.dependenteForm().valid()) {
      this.mostrarPendencias.set(true);
      this.dependenteForm().markAsTouched();
      return;
    }

    this.salvando.set(true);
    this.erroModal.set(null);

    this.meusDependentes
      .adicionar(this.dependenteNovo())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.salvando.set(false)),
      )
      .subscribe({
        next: () => {
          // Fecha ANTES de avisar: com o modal aberto o alerta ficaria atrás dele.
          this.modal()?.nativeElement.close();

          void this.alerta.sucesso({ titulo: 'Dependente adicionado', autoFechar: 4000 });
        },
        error: (erro: unknown) => this.erroModal.set(mensagemErroDependente(erro, 'salvar')),
      });
  }

  async removerDependente(dependente: DependenteResponse): Promise<void> {
    if (this.removendo() !== null) return;

    const confirmou = await this.alerta.confirmar({
      titulo: 'Remover dependente?',
      texto: `${dependente.desNomeDpd} deixará de estar vinculado ao seu cadastro.`,
      confirmar: 'Remover',
      tom: 'perigo',
    });

    if (!confirmou) return;

    this.removendo.set(dependente.codDependenteDpd);

    this.meusDependentes
      .remover(dependente.codDependenteDpd)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.removendo.set(null)),
      )
      .subscribe({
        next: () => {
          // O chip some com a lista recarregada: o foco vai para um lugar que continua na
          // tela — e é para lá que o alerta o devolve ao fechar.
          this.botaoAdicionar()?.nativeElement.focus();

          void this.alerta.sucesso({ titulo: 'Dependente removido', autoFechar: 4000 });
        },
        error: (erro: unknown) => {
          const { mensagem, detalhes } = mensagemErroDependente(erro, 'remover');

          void this.alerta.erro({
            titulo: 'Dependente não removido',
            texto: [mensagem, ...detalhes].join('\n'),
          });
        },
      });
  }
}
